/**
 * Telegram Connect Component
 * Form nhập API Token để kết nối Bot Telegram
 */
import React, { useState } from 'react';
import { fetchData } from '../../../lib/apis';
import { notification, Button, Spin, Card, Input, Alert, Result, Form } from 'antd';
import { SendOutlined, RobotOutlined } from '@ant-design/icons';

const getDefaultWebhookBaseUrl = () => {
    if (typeof window === 'undefined' || !window.location) return '';

    const { origin, hostname, protocol } = window.location;
    if (!origin) return '';
    if (protocol !== 'https:') return '';
    if (hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '0.0.0.0') return '';

    return origin;
};

const TelegramConnect = ({ onSuccess, onCancel, companyId }) => {
    const [loading, setLoading] = useState(false);
    const [step, setStep] = useState(1); // 1: input, 2: success, 3: error
    const [error, setError] = useState(null);
    const [botInfo, setBotInfo] = useState(null);

    const onFinish = async (values) => {
        setLoading(true);
        setError(null);
        try {
            const response = await fetchData({
                url: 'api/v1/telegram/connect',
                method: 'POST',
                data: { 
                    token: values.token,
                    company_id: companyId,
                    webhook_base_url: values.webhook_base_url
                }
            });

            if (response.code === 200) {
                setBotInfo(response.data);
                setStep(2);
                notification.success({
                    message: 'Thành công',
                    description: response.data?.webhook_warning || 'Đã kết nối Bot Telegram thành công!'
                });
                if (onSuccess) {
                    setTimeout(() => onSuccess(response.data), 1500);
                }
            } else {
                throw new Error(response.message || 'Không thể kết nối Bot');
            }
        } catch (err) {
            setError(err.message || 'Lỗi kết nối server');
            setStep(3);
        } finally {
            setLoading(false);
        }
    };

    const renderContent = () => {
        if (step === 1) {
            return (
                <Form layout="vertical" onFinish={onFinish}>
                    <Alert
                        message="Hướng dẫn"
                        description={
                            <ul>
                                <li>Mở Telegram và tìm kiếm <b>@BotFather</b></li>
                                <li>Dùng lệnh <code>/newbot</code> để tạo bot mới</li>
                                <li>Copy đoạn <b>API Token</b> và dán vào bên dưới</li>
                                <li>Nếu đang chạy local/tunnel, nhập đúng <b>Webhook Base URL</b> public hiện tại</li>
                            </ul>
                        }
                        type="info"
                        showIcon
                        style={{ marginBottom: 20 }}
                    />
                    
                    <Form.Item
                        label="HTTP API Token"
                        name="token"
                        rules={[{ required: true, message: 'Vui lòng nhập Bot Token!' }]}
                    >
                        <Input.Password 
                            placeholder="123456789:ABCDefGhIJKlmNoPQRsTUVwXyZ" 
                            prefix={<SendOutlined />} 
                        />
                    </Form.Item>

                    <Form.Item
                        label="Webhook Base URL"
                        name="webhook_base_url"
                        initialValue={getDefaultWebhookBaseUrl()}
                        extra="URL public HTTPS để Telegram gọi về. Ví dụ: https://xxxx.loca.lt"
                    >
                        <Input placeholder="https://your-public-domain.com" />
                    </Form.Item>

                    <div style={{ textAlign: 'right', marginTop: 16 }}>
                        <Button style={{ marginRight: 8 }} onClick={onCancel}>
                            Hủy
                        </Button>
                        <Button type="primary" htmlType="submit" loading={loading} icon={<SendOutlined />}>
                            Kết nối ngay
                        </Button>
                    </div>
                </Form>
            );
        }

        if (step === 2) {
            return (
                <Result
                    status="success"
                    title="Kết nối thành công!"
                    subTitle={`Đã kết nối Bot: @${botInfo?.username} (${botInfo?.full_name})`}
                    extra={[
                        <Button type="primary" key="close" onClick={onCancel}>
                            Hoàn tất
                        </Button>
                    ]}
                />
            );
        }

        if (step === 3) {
            return (
                <Result
                    status="error"
                    title="Kết nối thất bại"
                    subTitle={error || 'Vui lòng kiểm tra lại mã Token của bạn'}
                    extra={[
                        <Button type="primary" key="retry" onClick={() => setStep(1)}>
                            Thử lại
                        </Button>,
                        <Button key="cancel" onClick={onCancel}>
                            Đóng
                        </Button>
                    ]}
                />
            );
        }
    };

    return (
        <Card 
            title={
                <span>
                    <RobotOutlined style={{ marginRight: 8, color: '#0088cc' }} />
                    Kết nối Telegram Bot
                </span>
            }
            bordered={false}
        >
            <Spin spinning={loading && step === 1}>
                {renderContent()}
            </Spin>
        </Card>
    );
};

export default TelegramConnect;
